import { AuthGuard } from '@/components/auth/auth-guard';
import { AppLayout } from '@/components/layout';
import { VacancyConstructor } from '@/features/recruitment/components/vacancy-constructor';
import { VacancyStatusPill } from '@/features/recruitment/components/vacancy-status-pill';
import { useUpdateVacancy, useVacancy } from '@/hooks/use-recruitment';
import { canViewVacancies } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import type { UpdateVacancyInput } from '@/types/recruitment';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';

type VacancyDetailSearch = {
  q?: string;
  scroll?: number;
};

export const Route = createFileRoute('/recruitment/vacancies_/$id')({
  beforeLoad: () => {
    const checkGate = () => {
      const { isAuthenticated, user } = useAuthStore.getState();
      if (!isAuthenticated) throw redirect({ to: '/login' });
      if (!canViewVacancies(user)) throw redirect({ to: '/people' });
    };
    if (useAuthStore.getState().isInitialized) {
      checkGate();
      return;
    }
    return useAuthStore.getState().initialize().then(checkGate);
  },
  validateSearch: (search: Record<string, unknown>): VacancyDetailSearch => {
    const out: VacancyDetailSearch = {};
    if (typeof search.q === 'string' && search.q.length > 0) out.q = search.q;
    if (typeof search.scroll === 'number' && Number.isFinite(search.scroll)) {
      out.scroll = Math.max(0, Math.round(search.scroll));
    }
    return out;
  },
  component: EditVacancyPage,
});

function EditVacancyPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: vacancy, isLoading, error } = useVacancy(id);
  const updateMutation = useUpdateVacancy({ suppressGlobalToast: true });

  const handleBack = () => navigate({ to: '/recruitment/vacancies' });

  const handleSave = async (data: UpdateVacancyInput) => {
    await updateMutation.mutateAsync({ id, ...data });
  };

  return (
    <AuthGuard>
      <AppLayout>
        {/* Page Header */}
        <header className="sticky top-14 z-30 flex h-12 items-center border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
          <div className="flex min-w-0 items-center gap-2 text-sm text-muted-foreground">
            <span>Home</span>
            <span>/</span>
            <span>Recruitment</span>
            <span>/</span>
            <button
              onClick={handleBack}
              className="hover:text-foreground transition-colors cursor-pointer"
            >
              Vacancies
            </button>
            <span>/</span>
            <span className="max-w-xs truncate font-medium text-foreground" title={vacancy?.title}>
              {isLoading ? 'Loading...' : (vacancy?.title ?? 'Not found')}
            </span>
          </div>
        </header>

        {/* Main content */}
        <div className="flex h-[calc(100vh-6.5rem)] flex-col overflow-hidden">
          <div className="shrink-0 px-6 pt-6">
            <button
              onClick={handleBack}
              className="mb-5 inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors -ml-2 cursor-pointer"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to Vacancies
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-muted-foreground">Loading...</p>
            </div>
          ) : error || !vacancy ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-destructive">Vacancy not found</p>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col">
              <div className="shrink-0 px-6">
                <div className="mb-6 flex items-center gap-3">
                  <h1
                    className="truncate text-xl font-semibold tracking-tight text-foreground"
                    title={vacancy.title}
                  >
                    {vacancy.title}
                  </h1>
                  {/* shrink-0: a long title must give way to the pill, not squeeze its label */}
                  <div className="shrink-0">
                    <VacancyStatusPill vacancyId={vacancy.id} status={vacancy.status} />
                  </div>
                </div>
              </div>

              <VacancyConstructor
                vacancy={vacancy}
                onSave={handleSave}
                onCancel={handleBack}
                isSaving={updateMutation.isPending}
              />
            </div>
          )}
        </div>
      </AppLayout>
    </AuthGuard>
  );
}
